import { Injectable, inject } from '@angular/core';
import { FeaturesCollection } from '@principle-theorem/feature-flags';
import { NG_FEATURE_FLAGS_CONFIG } from '@principle-theorem/ng-feature-flags';
import { LoggerService } from '@principle-theorem/ng-shared';
import {
  Organisation,
  OrganisationSubscription,
  SubscriptionAddOns,
} from '@principle-theorem/principle-core';
import {
  type AddOnKey,
  type IOrganisation,
  type IOrganisationSubscription,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  Firestore,
  asReffable,
  getError,
  shareReplayCold,
} from '@principle-theorem/shared';
import { type Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';

/**
 * Options for the live-feature gate.
 */
interface IIsFeatureLiveOptions {
  /** Feature flag label as registered in the `FeaturesCollection`. */
  featureLabel: string;
  /** Add-on key that must be enabled for the gate to open. */
  addOnKey: AddOnKey;
}

/**
 * Angular reactive consumer API for Stripe product addOns.
 *
 * Combines two gates into a single boolean stream:
 *  1. Feature flag opt-in for the target organisation (label-based).
 *  2. Stripe add-on (resolved through the tristate override).
 *
 * The stream emits `false` while data is still loading so callers can render
 * fail-closed UI during the initial paint. Once both data sources have emitted,
 * the gate reflects the AND of (feature opt-in) AND (enabled add-on).
 *
 * AddOns are delineated by SUBSCRIPTION, so the gate resolves against
 * the single subscription covering the target practice — never a union across
 * the org's subscriptions, which would grant a sibling practice a product it
 * did not pay for.
 *
 * MUST NOT be used to invoke the Cloud Function `addOnsForPractice`
 * Promise API — that surface is reserved for backend code paths.
 */
@Injectable({ providedIn: 'root' })
export class AddOnService {
  private readonly _logger = inject(LoggerService);
  private readonly _featureFlagsConfig = inject(NG_FEATURE_FLAGS_CONFIG);
  private readonly _features = new FeaturesCollection(
    this._featureFlagsConfig.features,
    this._featureFlagsConfig.production
  );

  /**
   * Entry point for upcoming gated features; intentionally unused until then.
   * Reactive boolean that is `true` only when the feature flag is opted-in
   * for the org AND the add-on is currently enabled (after applying
   * any tristate `IAddOnOverride`).
   *
   * Emits `false` initially (loading) and dedupes consecutive equal booleans
   * via `distinctUntilChanged`. Concurrent subscribers share one Firestore
   * listener through `shareReplayCold()`, but no instance-level cache is
   * retained — calling this method again (e.g. after an organisation switch)
   * yields a fresh stream so a stale `false` value cannot leak across orgs.
   */
  isFeatureActiveForPractice$(
    orgRef: DocumentReference<IOrganisation>,
    practiceRef: DocumentReference<IPractice> | undefined,
    options: IIsFeatureLiveOptions
  ): Observable<boolean> {
    const featureEnabled$ = this._optInFeaturesFor$(orgRef).pipe(
      map((optInFeatures) =>
        this._features.isFeatureEnabledByLabel(
          options.featureLabel,
          optInFeatures
        )
      )
    );

    const enabled$ = this._subscriptionsFor$(orgRef).pipe(
      map((subscriptions) => {
        const subscription = OrganisationSubscription.selectForPractice(
          subscriptions,
          practiceRef
        );
        return SubscriptionAddOns.enabledKeys(subscription).has(
          options.addOnKey
        );
      })
    );

    return combineLatest([featureEnabled$, enabled$]).pipe(
      map(([featureEnabled, addOnEnabled]) => featureEnabled && addOnEnabled),
      startWith(false),
      distinctUntilChanged(),
      shareReplayCold()
    );
  }

  /**
   * Reactive stream of the org's `optInFeatures`. Returns `[]` when the
   * organisation document does not exist or the field is unset; swallows
   * read errors as `[]` so the gate fails closed rather than throwing.
   */
  protected _optInFeaturesFor$(
    orgRef: DocumentReference<IOrganisation>
  ): Observable<string[]> {
    return Firestore.safeDoc$<IOrganisation>(orgRef).pipe(
      map((organisation) => organisation?.optInFeatures ?? []),
      catchError((error: unknown) => {
        void this._logger.log(
          'ERROR',
          'Failed to read organisation feature flags',
          {
            error: getError(error),
          }
        );
        return of<string[]>([]);
      })
    );
  }

  /**
   * Reactive stream of the org's (undeleted) subscription documents. The
   * canonical path is `organisations/{orgId}/subscriptions`. Read errors
   * are swallowed as `[]` to fail closed.
   */
  protected _subscriptionsFor$(
    orgRef: DocumentReference<IOrganisation>
  ): Observable<IOrganisationSubscription[]> {
    return Organisation.subscriptions$(asReffable(orgRef)).pipe(
      catchError((error: unknown) => {
        void this._logger.log(
          'ERROR',
          'Failed to read organisation subscriptions',
          {
            error: getError(error),
          }
        );
        return of<IOrganisationSubscription[]>([]);
      })
    );
  }
}
